import { intl } from 'lang/intl'
import { isEmpty, partial } from 'lodash-es'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { Field, reduxForm } from 'redux-form/dist/redux-form'
import { TtIcon, TtIconButton } from '@23/react'
import { useWebinarColors } from '../../hooks/useWebinarColors'
import {
  WebinarSidebarSeparator,
  WebinarPanelComponent,
} from '../../features/WebinarSidebarFeature/components'

import {
  Button as ButtonComponent,
  ReactionsComponent,
  ReduxFormSimpleFieldComponent,
  TextareaAutoResizeComponent,
} from '..'

import { modifyDisabledTextColor, reduxFormValidators } from '../../utils'

import './CommentsFormComponent.scss'

const { required } = reduxFormValidators

const handleKeyDown = (handleSubmit, e) => {
  if (e.keyCode === 13 && e.shiftKey === false) {
    handleSubmit(e)
  }
}

const autoResizeProps = { initialHeight: '40px', maxHeight: '76px' }
const postIcon = { name: 'pv-send', color: 'fff', size: [20, 20] }

const getSubmitPromise = (onSubmit, values) =>
  new Promise((resolve, reject) => {
    onSubmit(values, { resolve, reject })
  })

const fieldPlaceholderForCommentType = (commentType, chatRoom) => {
  //
  // This switch statement should always stay exhaustive and never hit the default case.
  //
  switch (commentType) {
    case 'question':
      return intl.formatMessage({
        defaultMessage: 'Ask a question to the speakers',
      })
    case 'chat':
      switch (chatRoom) {
        case 'private':
          return intl.formatMessage({
            defaultMessage: 'Chat with the other speakers and hosts.',
          })
        case 'audience':
        default:
          return intl.formatMessage({
            defaultMessage: 'Write your message here...',
          })
      }
    default:
      return ''
  }
}

const CommentsFormComponent = (props) => {
  const {
    invalid,
    submitting,
    pristine,
    onSubmit,
    handleSubmit,
    askForName,
    isAutoResizeTextarea,
    buttonText,
    hasButtonIcon,
    commentType,
    styles: { inputStyle, buttonStyle },
    onReactionClicked,
    hasReactionsEnabled,
    chatRoom,
  } = props

  const isButtonDisabled = invalid || submitting || pristine
  const customInputPropsStyle = { style: inputStyle }
  const handleFormSubmit = handleSubmit(partial(getSubmitPromise, onSubmit))
  const textAreaComponent = isAutoResizeTextarea
    ? TextareaAutoResizeComponent
    : ReduxFormSimpleFieldComponent
  const { tabs, accent } = useWebinarColors()

  const [name, setName] = useState(window.localStorage?.comment_name)

  const placeholderText = intl.formatMessage({
    defaultMessage: 'Type in your name...',
  })

  const ariaLabelForSend = intl.formatMessage({
    defaultMessage: 'Send message.',
  })

  if (commentType === 'chat') {
    const styles = {
      '--chat-form-input-text-color': tabs.highContrast.color,
      '--chat-form-input-placeholder-color':
        tabs.highContrast.transparent('50%'),
      '--chat-form-input-placeholder-color-disabled':
        tabs.highContrast.transparent('20%'),
      '--chat-form-button-color': accent.contrast.color,
      '--chat-form-button-color-disabled': tabs.contrast.transparent('20%'),
    }

    return (
      <>
        <WebinarSidebarSeparator isFullWidth />
        <div className="chat-form-component">
          <form
            className="chat-form"
            onSubmit={handleFormSubmit}
            style={styles}
          >
            {askForName && (
              <div>
                <div className="name-wrapper enclosure">
                  <Field
                    name="name"
                    type="text"
                    validate={required}
                    component={ReduxFormSimpleFieldComponent}
                    className="field-component"
                    placeholder={placeholderText}
                    forceHideError
                    customInputProps={{
                      autoComplete: 'off',
                    }}
                    onChange={(_, value) => {
                      setName(value)
                    }}
                  />
                </div>
              </div>
            )}
            <div>
              {hasReactionsEnabled && (
                <div className="reactions-wrapper enclosure">
                  <ReactionsComponent
                    onClicked={onReactionClicked}
                    isDisabled={askForName && isEmpty(name)}
                  />
                </div>
              )}
              <div className="message-wrapper enclosure">
                <Field
                  name="content"
                  component={ReduxFormSimpleFieldComponent}
                  type="text"
                  className="message field-component"
                  placeholder={fieldPlaceholderForCommentType(
                    commentType,
                    chatRoom
                  )}
                  validate={required}
                  forceHideError
                  customInputProps={{
                    autoComplete: 'off',
                    onKeyDown: partial(handleKeyDown, handleFormSubmit),
                  }}
                />
                <button
                  type="submit"
                  disabled={isButtonDisabled}
                  aria-label="Send"
                >
                  <TtIcon name="send" />
                </button>
              </div>
            </div>
          </form>
        </div>
      </>
    )
  }

  if (commentType === 'question') {
    const styles = {
      '--chat-form-input-text-color': tabs.highContrast.color,
      '--chat-form-input-placeholder-color':
        tabs.highContrast.transparent('50%'),
      '--chat-form-input-placeholder-color-disabled':
        tabs.highContrast.transparent('20%'),
      '--chat-form-button-color': tabs.contrast.transparent('87%'),
      '--chat-form-button-color-disabled': tabs.contrast.transparent('20%'),
    }

    return (
      <div className="question-form-component">
        <form
          className="questions-form"
          onSubmit={handleFormSubmit}
          style={styles}
        >
          {askForName && (
            <WebinarPanelComponent>
              <Field
                name="name"
                type="text"
                validate={required}
                component={ReduxFormSimpleFieldComponent}
                className="field-component"
                placeholder={placeholderText}
                forceHideError
                customInputProps={{
                  autoComplete: 'off',
                }}
                onChange={(_, value) => {
                  setName(value)
                }}
              />
            </WebinarPanelComponent>
          )}
          <WebinarPanelComponent>
            <Field
              name="content"
              component={ReduxFormSimpleFieldComponent}
              type="text"
              className="message field-component"
              placeholder={fieldPlaceholderForCommentType(
                commentType,
                chatRoom
              )}
              validate={required}
              forceHideError
              customInputProps={{
                autoComplete: 'off',
                onKeyDown: partial(handleKeyDown, handleFormSubmit),
              }}
            />
            <TtIconButton
              name="send"
              label="Ask a question to the speakers"
              type="submit"
              disabled={isButtonDisabled}
            />
          </WebinarPanelComponent>
        </form>
      </div>
    )
  }

  return (
    <div className="comments-form-component">
      <form className="comments-form" onSubmit={handleFormSubmit}>
        {askForName && (
          <div className="name-input-wrapper">
            <Field
              name="name"
              type="text"
              validate={required}
              component={ReduxFormSimpleFieldComponent}
              placeholder={placeholderText}
              forceHideError
              customInputProps={customInputPropsStyle}
              onChange={(_, value) => {
                setName(value)
              }}
            />
          </div>
        )}
        <div className="other-fields">
          {commentType === 'chat' && hasReactionsEnabled && (
            <div className="reactions-wrapper">
              <ReactionsComponent
                onClicked={onReactionClicked}
                isDisabled={askForName && isEmpty(name)}
              />
            </div>
          )}
          <Field
            name="content"
            component={textAreaComponent}
            type="textarea"
            className="message"
            placeholder={fieldPlaceholderForCommentType(commentType, chatRoom)}
            validate={required}
            forceHideError
            {...(isAutoResizeTextarea ? autoResizeProps : {})}
            customInputProps={{
              autoComplete: 'off',
              onKeyDown: partial(handleKeyDown, handleFormSubmit),
              ...customInputPropsStyle,
            }}
          />
          <div className="button-wrapper">
            <ButtonComponent
              key="submit"
              type="submit"
              className="post-chat-question"
              disabled={isButtonDisabled}
              {...(hasButtonIcon ? { iconConfig: postIcon } : {})}
              style={modifyDisabledTextColor(buttonStyle, isButtonDisabled)}
              ariaLabel={ariaLabelForSend}
              round
            >
              {buttonText}
            </ButtonComponent>
          </div>
        </div>
      </form>
    </div>
  )
}

CommentsFormComponent.propTypes = {
  onSubmit: PropTypes.func,
  pristine: PropTypes.bool,
  isAutoResizeTextarea: PropTypes.bool,
  askForName: PropTypes.bool,
  styles: PropTypes.shape({
    inputStyle: PropTypes.object,
    buttonStyle: PropTypes.object,
  }),
  commentType: PropTypes.oneOf(['question', 'chat']),
  // redux-form props
  dirty: PropTypes.bool,
  invalid: PropTypes.bool,
  initialize: PropTypes.func,
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  buttonText: PropTypes.string,
  hasButtonIcon: PropTypes.bool,
  onReactionClicked: PropTypes.func,
  hasReactionsEnabled: PropTypes.bool,
  chatRoom: PropTypes.string,
}

CommentsFormComponent.defaultProps = {
  isAutoResizeTextarea: false,
  styles: {},
  hasButtonIcon: true,
  commentType: 'question',
  hasReactionsEnabled: true,
}

export default reduxForm()(CommentsFormComponent)
